import {useQuery, type QueryKey} from '@tanstack/react-query'
import {useMemo, useCallback} from 'react'
import {useServices} from '@/services/context'
import type {Identifier, Identity} from '@/types/identity'
import type {AccessProfileAssignmentListItem} from '@/types/assignment'
import type {FunctionGroupAssignment, InheritedAccessProfileAssignment} from '@/types/function-group'
import type {Account} from '@/types/account'
import type {TimelineResponse, TimelineEvent} from '@/types/timeline'

export const IDENTITY_KEY = 'identity-detail' as const
export const IDENTITIES_KEY = 'identity-list' as const

export const IDENTITY_SUBKEY_ASSIGNMENTS = 'assignments' as const
export const IDENTITY_SUBKEY_FG_ASSIGNMENTS = 'fg-assignments' as const
export const IDENTITY_SUBKEY_INHERITED_APS = 'inherited-ap-assignments' as const
export const IDENTITY_SUBKEY_ACCOUNTS = 'accounts' as const
export const IDENTITY_SUBKEY_IDENTIFIERS = 'identifiers' as const
export const IDENTITY_SUBKEY_TIMELINE = 'timeline' as const

const TIMELINE_PAGE_SIZE = 100

export interface UseIdentityResult {
    identity: Identity | undefined
    assignments: AccessProfileAssignmentListItem[]
    fgAssignments: FunctionGroupAssignment[]
    inheritedAssignments: InheritedAccessProfileAssignment[]
    inheritedLoading: boolean
    inheritedError: Error | null
    inheritedRefetch: () => void
    accounts: Account[]
    identifiers: Identifier[]
    timeline: TimelineEvent[]
    timelineLoading: boolean
    isLoading: boolean
    error: Error | null
    accountsQueryKey: QueryKey
    queryKeyFor: (sub?: string) => QueryKey
}

/**
 * Composite read hook for the IdentityDetail page. Bundles the seven queries
 * the detail body relies on so the page never has to derive query keys
 * inline. Returns the bare slices the page renders plus the loading / error
 * flags for the primary identity fetch.
 *
 * The two ancillary slices the page treats specially keep their own flags:
 * {@code inheritedLoading} / {@code inheritedError} / {@code inheritedRefetch}
 * back the inline retry banner; {@code timelineLoading} drives the access
 * timeline skeleton independently of the primary fetch.
 *
 * Pass {@code id} as {@code undefined} during the brief gap before
 * {@code useParams} resolves to keep the hook safe under test renders.
 */
export function useIdentity(id: string | undefined): UseIdentityResult {
    const services = useServices()

    const queryKeyFor = useCallback((sub?: string): QueryKey => {
        return sub ? [IDENTITY_KEY, id, sub] : [IDENTITY_KEY, id]
    }, [id])

    const accountsQueryKey = useMemo<QueryKey>(() => [IDENTITY_KEY, id, IDENTITY_SUBKEY_ACCOUNTS], [id])

    const identityQuery = useQuery<Identity>({
        queryKey: [IDENTITY_KEY, id],
        queryFn: () => services.identities.detail(id!),
        enabled: !!id,
    })

    const assignmentsQuery = useQuery<AccessProfileAssignmentListItem[]>({
        queryKey: [IDENTITY_KEY, id, IDENTITY_SUBKEY_ASSIGNMENTS],
        queryFn: () => services.identities.assignments(id!),
        enabled: !!id,
    })

    const fgAssignmentsQuery = useQuery<FunctionGroupAssignment[]>({
        queryKey: [IDENTITY_KEY, id, IDENTITY_SUBKEY_FG_ASSIGNMENTS],
        queryFn: () => services.functionGroupAssignments.byIdentity(id!),
        enabled: !!id,
    })

    const inheritedQuery = useQuery<InheritedAccessProfileAssignment[]>({
        queryKey: [IDENTITY_KEY, id, IDENTITY_SUBKEY_INHERITED_APS],
        queryFn: () => services.identities.inheritedAccessProfileAssignments(id!),
        enabled: !!id,
    })

    const accountsQuery = useQuery<Account[]>({
        queryKey: accountsQueryKey,
        queryFn: () => services.identities.accounts(id!),
        enabled: !!id,
    })

    const identifiersQuery = useQuery<Identifier[]>({
        queryKey: [IDENTITY_KEY, id, IDENTITY_SUBKEY_IDENTIFIERS],
        queryFn: () => services.identities.identifiers(id!),
        enabled: !!id,
    })

    const timelineQuery = useQuery<TimelineResponse>({
        queryKey: [IDENTITY_KEY, id, IDENTITY_SUBKEY_TIMELINE],
        queryFn: () => services.identities.timeline(id!, {size: TIMELINE_PAGE_SIZE}),
        enabled: !!id,
    })

    return {
        identity: identityQuery.data,
        assignments: assignmentsQuery.data ?? [],
        fgAssignments: fgAssignmentsQuery.data ?? [],
        inheritedAssignments: inheritedQuery.data ?? [],
        inheritedLoading: inheritedQuery.isLoading,
        inheritedError: (inheritedQuery.error as Error | null) ?? null,
        inheritedRefetch: () => { void inheritedQuery.refetch() },
        accounts: accountsQuery.data ?? [],
        identifiers: identifiersQuery.data ?? [],
        timeline: timelineQuery.data?.content ?? [],
        timelineLoading: timelineQuery.isLoading,
        isLoading: identityQuery.isLoading,
        error: (identityQuery.error as Error | null) ?? null,
        accountsQueryKey,
        queryKeyFor,
    }
}
